import { Component } from '@theme/component';

/**
 * Category bar: adds to the CSS-only dropdown the three things CSS cannot do.
 *
 * The panel opens on `:hover` and `:focus-within` in the stylesheet, so the
 * menu works with mouse and keyboard before this module loads — and if it
 * never loads. What is left here:
 *
 * 1. Escape. Closing means overriding `:focus-within`, because focus has to
 *    land back on the trigger, and the trigger lives inside the item that
 *    `:focus-within` matches. Hence the `data-closed` attribute, dropped again
 *    as soon as focus or the pointer leaves the item.
 * 2. `aria-expanded`. The open state is a CSS state, which the DOM cannot see;
 *    without this the attribute would sit at `false` while the panel is wide
 *    open and tell screen readers the opposite of what is on screen.
 * 3. Touch. A touch device has no hover, so tapping a parent category would
 *    navigate straight to the collection and the shopper would never see the
 *    subcategories. Here the first tap opens, the second follows the link, and
 *    the panel header links to that same collection anyway.
 */
class MenuCategoriasComponent extends Component {
  /** Item opened by tap — the only state the script owns. @type {HTMLElement | null} */
  #tapped = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    document.addEventListener('click', this.#onDocumentClick);
    // A pinned panel would drift away from the bar as the page moves.
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('scroll', this.#onScroll);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #itemOf(target) {
    if (!(target instanceof Element)) return null;

    return /** @type {HTMLElement | null} */ (target.closest('.menu-categorias__item--parent'));
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    item.querySelector('.menu-categorias__top')?.setAttribute('aria-expanded', String(expanded));
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const item = this.#itemOf(event.target) ?? this.#tapped;
    if (!item) return;

    this.#closeTapped();
    item.setAttribute('data-closed', '');
    this.#setExpanded(item, false);

    // Focus must leave the panel, or the panel keeps the focus it is hiding.
    /** @type {HTMLElement | null} */ (item.querySelector('.menu-categorias__top'))?.focus();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // `hover: none` is the honest test for "this tap has no hover behind it".
    // Pointer type alone misses hybrid devices being used with a finger.
    if (window.matchMedia('(hover: hover)').matches) return;

    const trigger = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.menu-categorias__top') : null
    );
    if (!trigger) return;

    const item = this.#itemOf(trigger);
    if (!item || item === this.#tapped) return;

    // First tap reveals the panel instead of leaving the page.
    event.preventDefault();
    this.#closeTapped();

    // On narrow screens the panel is `fixed`, so it needs the bar's real
    // bottom edge: `inset-block-start: 100%` would resolve against the
    // viewport and drop the panel below the fold.
    this.style.setProperty('--menu-cat-panel-top', `${Math.max(this.getBoundingClientRect().bottom, 0)}px`);

    item.removeAttribute('data-closed');
    item.setAttribute('data-open', '');
    this.#setExpanded(item, true);
    this.#tapped = item;
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const item = this.#itemOf(event.target);
    if (item) this.#setExpanded(item, true);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const item = this.#itemOf(event.target);
    const next = /** @type {Node | null} */ (event.relatedTarget);

    if (item && !(next && item.contains(next))) {
      item.removeAttribute('data-closed');
      this.#setExpanded(item, false);
    }

    if (!(next && this.contains(next))) this.#closeTapped();
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    const item = this.#itemOf(event.target);
    if (!item) return;

    item.removeAttribute('data-closed');
    this.#setExpanded(item, true);
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    const item = this.#itemOf(event.target);
    if (!item) return;

    // Moving between children of the same item is not leaving it.
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && item.contains(next)) return;

    // Keyboard focus still inside keeps the panel open, and so does the label.
    if (item.contains(document.activeElement)) return;

    this.#setExpanded(item, false);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#closeTapped();
  };

  #onScroll = () => {
    this.#closeTapped();
  };

  #closeTapped() {
    if (!this.#tapped) return;

    this.#tapped.removeAttribute('data-open');
    this.#setExpanded(this.#tapped, false);
    this.#tapped = null;
  }
}

// The `-component` suffix is load-bearing: the theme's delegated event handling
// (component.js) only wires up custom elements whose tag ends in `-component`.
if (!customElements.get('menu-categorias-component')) {
  customElements.define('menu-categorias-component', MenuCategoriasComponent);
}
